import type {
	UpdateWidgetLinkArgs,
	UpdateWidgetSettingsArgs,
} from "@productlane/zero/types"
import {
	isAllowedWidgetLinkUrl,
	normalizeWidgetLinkUrl,
	WIDGET_LINK_URL_HINT,
} from "@productlane/lib/src/widget-link-url"

export interface WidgetLinkFormValue {
	id: string
	workspaceId: string
	title: string | null
	url: string | null
	icon: string | null
}

export interface StoredWidgetLink {
	id: string
	title: string | null
	url: string | null
	icon: string | null
	isEnabled: boolean | null
}

export interface InvalidWidgetLink {
	index: number
	link: WidgetLinkFormValue
}

const toLinkWrite = (link: WidgetLinkFormValue): UpdateWidgetLinkArgs => ({
	id: link.id,
	workspaceId: link.workspaceId,
	title: link.title ?? "",
	url: normalizeWidgetLinkUrl(link.url),
	isEnabled: !!link.title,
	icon: link.icon ?? "",
})

const matchesStored = (
	write: UpdateWidgetLinkArgs,
	stored: StoredWidgetLink | undefined,
) =>
	!!stored &&
	write.title === (stored.title ?? "") &&
	write.url === normalizeWidgetLinkUrl(stored.url) &&
	write.icon === (stored.icon ?? "") &&
	write.isEnabled === !!stored.isEnabled

// A stored link nobody touched is left alone, so an old value the URL rule now
// rejects never blocks the rest of the form.
export function planWidgetLinkWrites(
	formLinks: WidgetLinkFormValue[],
	storedLinks: StoredWidgetLink[],
) {
	const storedById = new Map(storedLinks.map((link) => [link.id, link]))
	const writes: UpdateWidgetLinkArgs[] = []
	const invalid: InvalidWidgetLink[] = []

	formLinks.forEach((link, index) => {
		const write = toLinkWrite(link)
		if (matchesStored(write, storedById.get(link.id))) return
		if (!isAllowedWidgetLinkUrl(write.url)) {
			invalid.push({ index, link })
			return
		}
		writes.push(write)
	})

	return { writes, invalid }
}

export async function saveWidgetSettingsForm({
	settings,
	formLinks,
	storedLinks,
	updateSettings,
	updateLink,
}: {
	settings: UpdateWidgetSettingsArgs
	formLinks: WidgetLinkFormValue[]
	storedLinks: StoredWidgetLink[]
	updateSettings: (args: UpdateWidgetSettingsArgs) => Promise<unknown>
	updateLink: (args: UpdateWidgetLinkArgs) => Promise<unknown>
}) {
	await updateSettings(settings)
	const { writes, invalid } = planWidgetLinkWrites(formLinks, storedLinks)
	for (const write of writes) {
		await updateLink(write)
	}
	return { invalidLinks: invalid }
}

export function invalidWidgetLinkToast({ index, link }: InvalidWidgetLink) {
	const name = link.title?.trim() || `Custom link ${index + 1}`
	return {
		title: `"${name}" needs a full URL to save`,
		description: `"${normalizeWidgetLinkUrl(link.url)}" isn't a link the widget can open. ${WIDGET_LINK_URL_HINT}`,
	}
}

const FIELD_LABELS: Record<string, string> = {
	widgetIconType: "Launcher icon",
	widgetSize: "Launcher size",
	defaultView: "Default view",
}

// Names the first field that blocked the save, so the toast can say which one.
export function invalidWidgetFieldToast(errors: Record<string, string>) {
	const [field] = Object.keys(errors)
	const label = (field && FIELD_LABELS[field]) || "A widget setting"
	return {
		title: `${label} has a value we can't save`,
		description: "Pick another option to save your changes.",
	}
}
